import type { RetrievalResult } from "@/lib/retrieval";

/**
 * Hands a finished dashboard lookup to the report page through
 * sessionStorage, so opening a report seconds after a lookup renders
 * instantly instead of re-retrieving every layer live (~30s cold) — the
 * 2026-08-29 audit's report-page finding. One slot, latest lookup wins;
 * the report falls back to a live retrieval when the slot is missing,
 * mismatched, or stale, and always offers re-retrieval.
 */
const KEY = "prosa:lookup-handoff";
const MAX_AGE_MS = 15 * 60 * 1000;

export type LookupHandoff = {
  address: string;
  topic: string;
  result: RetrievalResult;
  storedAt: number;
};

const norm = (s: string) => s.trim().toLowerCase();

export function saveLookupHandoff(
  address: string,
  topic: string,
  result: RetrievalResult,
): void {
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ address, topic, result, storedAt: Date.now() }),
    );
  } catch {
    // Storage unavailable (private mode, quota): the report just re-retrieves.
  }
}

export function loadLookupHandoff(
  address: string,
  topic: string,
): LookupHandoff | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const handoff = JSON.parse(raw) as LookupHandoff;
    if (
      norm(handoff.address) !== norm(address) ||
      norm(handoff.topic) !== norm(topic) ||
      Date.now() - handoff.storedAt > MAX_AGE_MS ||
      !handoff.result?.layers?.length
    ) {
      return null;
    }
    return handoff;
  } catch {
    return null;
  }
}
